import { DynamoDBClient, DeleteItemCommand, GetItemCommand, PutItemCommand, QueryCommand, UpdateItemCommand } from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import { MeetingData, MeetingAttendee, RecentMeeting, Employee, RepeatOption, SortOption } from '../interfaces/interface';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { CognitoService } from 'src/services/aws/CognitoService';
import { MEETING_CONSTANTS } from '../constants/EAHuddleConstants';

const stage = ENVIRONMENT_VARIABLES.env.Stage;
const normalizedStage = stage.toLowerCase() === 'dev' ? 'alpha' : stage.toLowerCase();
const TABLE_NAME = `finsuite_ea_huddle_employee_settings_${normalizedStage}`;

export class EmployeeSettingsService {
  private static client: DynamoDBClient | null = null;

  private static getClient(): DynamoDBClient {
    if (this.client) return this.client;
    this.client = new DynamoDBClient({
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  // ── Favorites ────────────────────────────────────────────────────────

  /**
   * Retrieve the list of favorite login names for a given employee alias.
   */
  public static async getFavorites(employeeAlias: string): Promise<string[]> {
    const client = this.getClient();
    const result = await client.send(
      new GetItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: employeeAlias,
          sk: 'FAVORITES'
        })
      })
    );

    if (!result.Item) {
      return [];
    }

    const item = unmarshall(result.Item);
    return item.loginNames ?? [];
  }

  /**
   * Save the list of favorite login names for a given employee alias.
   */
  public static async saveFavorites(employeeAlias: string, loginNames: string[]): Promise<void> {
    const client = this.getClient();
    await client.send(
      new PutItemCommand({
        TableName: TABLE_NAME,
        Item: marshall({
          employee_alias: employeeAlias,
          sk: 'FAVORITES',
          loginNames,
          updatedAt: new Date().toISOString()
        })
      })
    );
  }

  // ── Meeting persistence ──────────────────────────────────────────────

  private static readonly MAX_RECENT_MEETINGS = MEETING_CONSTANTS.MAX_RECENT_MEETINGS;

  /**
   * Save full meeting data (attendees, subject, etc.).
   * Stored under pk=creatorAlias, sk=MEETING#<meetingId>.
   */
  public static async saveMeeting(creatorAlias: string, meeting: MeetingData): Promise<void> {
    const client = this.getClient();
    await client.send(
      new PutItemCommand({
        TableName: TABLE_NAME,
        Item: marshall(
          {
            employee_alias: creatorAlias,
            sk: `MEETING#${meeting.meetingId}`,
            meetingId: meeting.meetingId,
            subject: meeting.subject,
            agenda: meeting.agenda ?? '',
            createdBy: meeting.createdBy,
            createdAt: meeting.createdAt,
            attendees: meeting.attendees,
            organizerLogin: meeting.organizerLogin,
            duration: meeting.duration,
            repeat: meeting.repeat,
            onBehalfOfLogin: meeting.onBehalfOfLogin ?? null,
            coOrganizerLogin: meeting.coOrganizerLogin ?? null,
            attachments: meeting.attachments ?? [],
            authorizedLogins: meeting.authorizedLogins ?? [],
            groupIds: meeting.groupIds ?? [],
            selectedSlotId: meeting.selectedSlotId ?? null,
            sortBy: meeting.sortBy ?? 'priority',
            ...(meeting.isDeleted ? { isDeleted: true, deletedAt: meeting.deletedAt } : {}),
            updatedAt: new Date().toISOString()
          },
          { removeUndefinedValues: true }
        )
      })
    );
  }

  /**
   * Retrieve a meeting by creator alias and meeting ID.
   * Anyone with the link can call this — auth is handled at the app level.
   */
  public static async getMeeting(creatorAlias: string, meetingId: string): Promise<MeetingData | null> {
    const client = this.getClient();
    const result = await client.send(
      new GetItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: creatorAlias,
          sk: `MEETING#${meetingId}`
        })
      })
    );

    if (!result.Item) {
      return null;
    }

    const item = unmarshall(result.Item);

    return {
      meetingId: item.meetingId,
      subject: item.subject ?? '',
      agenda: item.agenda ?? '',
      createdBy: item.createdBy ?? creatorAlias,
      createdAt: item.createdAt ?? '',
      attendees: (item.attendees ?? []) as MeetingAttendee[],
      organizerLogin: item.organizerLogin ?? creatorAlias,
      duration: item.duration ?? 30,
      repeat: (item.repeat as RepeatOption) ?? 'none',
      onBehalfOfLogin: item.onBehalfOfLogin ?? null,
      coOrganizerLogin: item.coOrganizerLogin ?? null,
      attachments: (item.attachments ?? []) as string[],
      authorizedLogins: item.authorizedLogins ? (item.authorizedLogins as string[]) : undefined,
      groupIds: item.groupIds ? (item.groupIds as Array<{ groupId: string; groupType: string }>) : undefined,
      selectedSlotId: (item.selectedSlotId as string) ?? null,
      sortBy: (item.sortBy as SortOption) ?? undefined,
      isDeleted: item.isDeleted ?? false,
      deletedAt: item.deletedAt as string | undefined
    };
  }

  /**
   * Atomically update scheduling metadata (subject, organizerLogin, duration, repeat)
   * on an existing meeting record without overwriting attendees.
   */
  public static async updateMeetingMetadata(
    creatorAlias: string,
    meetingId: string,
    metadata: {
      subject?: string;
      agenda?: string;
      organizerLogin?: string;
      coOrganizerLogin?: string | null;
      duration?: number;
      repeat?: RepeatOption;
      onBehalfOfLogin?: string | null;
      attachments?: string[];
      authorizedLogins?: string[];
      selectedSlotId?: string | null;
    }
  ): Promise<void> {
    const client = this.getClient();

    const expressionParts: string[] = [];
    const expressionNames: Record<string, string> = {};
    const expressionValues: Record<string, any> = {};

    if (metadata.subject !== undefined) {
      expressionParts.push('#subj = :subj');
      expressionNames['#subj'] = 'subject';
      expressionValues[':subj'] = metadata.subject;
    }
    if (metadata.agenda !== undefined) {
      expressionParts.push('agenda = :agenda');
      expressionValues[':agenda'] = metadata.agenda;
    }
    if (metadata.organizerLogin !== undefined) {
      expressionParts.push('organizerLogin = :org');
      expressionValues[':org'] = metadata.organizerLogin;
    }
    if (metadata.coOrganizerLogin !== undefined) {
      expressionParts.push('coOrganizerLogin = :coOrg');
      expressionValues[':coOrg'] = metadata.coOrganizerLogin;
    }
    if (metadata.duration !== undefined) {
      expressionParts.push('#dur = :dur');
      expressionNames['#dur'] = 'duration';
      expressionValues[':dur'] = metadata.duration;
    }
    if (metadata.repeat !== undefined) {
      expressionParts.push('#rpt = :rpt');
      expressionNames['#rpt'] = 'repeat';
      expressionValues[':rpt'] = metadata.repeat;
    }

    if (metadata.onBehalfOfLogin !== undefined) {
      expressionParts.push('onBehalfOfLogin = :obol');
      expressionValues[':obol'] = metadata.onBehalfOfLogin;
    }

    if (metadata.attachments !== undefined) {
      expressionParts.push('attachments = :att');
      expressionValues[':att'] = metadata.attachments;
    }

    if (metadata.authorizedLogins !== undefined) {
      expressionParts.push('authorizedLogins = :authLogins');
      expressionValues[':authLogins'] = metadata.authorizedLogins;
    }

    if (metadata.selectedSlotId !== undefined) {
      expressionParts.push('selectedSlotId = :selSlot');
      expressionValues[':selSlot'] = metadata.selectedSlotId;
    }

    if (expressionParts.length === 0) return;

    expressionParts.push('updatedAt = :updAt');
    expressionValues[':updAt'] = new Date().toISOString();

    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: creatorAlias,
          sk: `MEETING#${meetingId}`
        }),
        UpdateExpression: `SET ${expressionParts.join(', ')}`,
        ...(Object.keys(expressionNames).length > 0 ? { ExpressionAttributeNames: expressionNames } : {}),
        ExpressionAttributeValues: marshall(expressionValues)
      })
    );
  }

  /**
   * Retrieve recent meetings by querying all MEETING# sort key items.
   * Sorted by createdAt descending (most recent first), capped at MAX_RECENT_MEETINGS.
   */
  public static async getRecentMeetings(employeeAlias: string): Promise<RecentMeeting[]> {
    const client = this.getClient();
    const result = await client.send(
      new QueryCommand({
        TableName: TABLE_NAME,
        KeyConditionExpression: 'employee_alias = :pk AND begins_with(sk, :prefix)',
        ExpressionAttributeValues: marshall({
          ':pk': employeeAlias,
          ':prefix': 'MEETING#'
        })
      })
    );

    if (!result.Items || result.Items.length === 0) {
      return [];
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    // Filter to only actual meeting records (sk = "MEETING#<uuid>")
    // Exclude sub-records like "MEETING#<uuid>#TIME_SLOT#..." or "MEETING#<uuid>#COMMENTS"
    const meetingItems = result.Items.map((raw) => unmarshall(raw)).filter((item) => {
      const sk = item.sk as string;
      // A meeting record has exactly one '#' after 'MEETING#' — no additional segments
      const parts = sk.split('#');
      return parts.length === 2 && parts[0] === 'MEETING';
    });

    const meetings: RecentMeeting[] = meetingItems
      .map((item) => {
        const attendees = (item.attendees ?? []) as MeetingAttendee[];
        return {
          meetingId: item.meetingId ?? '',
          tinyUrl: `${origin}/ea-huddle?meetingId=${encodeURIComponent(item.meetingId ?? '')}&creator=${encodeURIComponent(
            item.createdBy ?? employeeAlias
          )}`,
          subject: item.subject ?? 'Untitled Meeting',
          attendees,
          createdBy: item.createdBy ?? employeeAlias,
          createdAt: item.createdAt ?? '',
          isDeleted: item.isDeleted ?? false
        };
      })
      .filter((m) => !m.isDeleted);

    // Sort most recent first and cap
    meetings.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return meetings.slice(0, this.MAX_RECENT_MEETINGS);
  }

  /**
   * Atomically append a single attachment S3 URI to the meeting record.
   * Uses DynamoDB list_append to avoid read-then-write race conditions.
   */
  public static async appendAttachment(creatorAlias: string, meetingId: string, s3Uri: string): Promise<void> {
    const client = this.getClient();
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: creatorAlias,
          sk: `MEETING#${meetingId}`
        }),
        UpdateExpression: 'SET attachments = list_append(if_not_exists(attachments, :empty), :newAtt), updatedAt = :updAt',
        ExpressionAttributeValues: marshall({
          ':empty': [],
          ':newAtt': [s3Uri],
          ':updAt': new Date().toISOString()
        })
      })
    );
  }

  /**
   * Remove a single attachment S3 URI from the meeting record.
   * Uses optimistic locking to prevent concurrent update conflicts.
   */
  public static async removeAttachment(creatorAlias: string, meetingId: string, s3Uri: string): Promise<void> {
    const client = this.getClient();
    const meeting = await this.getMeeting(creatorAlias, meetingId);
    if (!meeting) return;
    const rawOriginal = meeting.attachments;
    const original = rawOriginal ?? [];
    const filtered = original.filter((uri) => uri !== s3Uri);

    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({ employee_alias: creatorAlias, sk: `MEETING#${meetingId}` }),
        UpdateExpression: 'SET attachments = :filtered, updatedAt = :now',
        ConditionExpression: rawOriginal === undefined ? 'attribute_not_exists(attachments)' : 'attachments = :original',
        ExpressionAttributeValues: marshall(
          {
            ':filtered': filtered,
            ...(rawOriginal !== undefined && { ':original': rawOriginal }),
            ':now': new Date().toISOString()
          },
          { removeUndefinedValues: true }
        )
      })
    );
  }

  /**
   * Soft-delete a meeting by setting isDeleted=true.
   * The record is preserved in DynamoDB for audit/recovery purposes.
   */
  public static async deleteMeeting(creatorAlias: string, meetingId: string): Promise<void> {
    const client = this.getClient();
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: creatorAlias,
          sk: `MEETING#${meetingId}`
        }),
        UpdateExpression: 'SET isDeleted = :deleted, deletedAt = :deletedAt',
        ExpressionAttributeValues: marshall({
          ':deleted': true,
          ':deletedAt': new Date().toISOString()
        })
      })
    );
  }

  // ── Employee info cache ──────────────────────────────────────────────

  /**
   * Retrieve cached employee info by login name.
   * Returns null if not found or if the DynamoDB TTL has logically expired.
   *
   * Schema: pk=<login>, sk=EMPLOYEE_INFO
   */
  public static async getEmployeeInfo(loginName: string): Promise<Employee | null> {
    const client = this.getClient();
    const result = await client.send(
      new GetItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: loginName.toLowerCase(),
          sk: 'EMPLOYEE_INFO'
        })
      })
    );

    if (!result.Item) {
      return null;
    }

    const item = unmarshall(result.Item);

    // Check logical TTL (DynamoDB TTL deletion is eventually consistent)
    const ttl = item.ttl as number | undefined;
    if (ttl && ttl < Math.floor(Date.now() / 1000)) {
      return null;
    }

    return (item.employeeData as Employee) ?? null;
  }

  /**
   * Save employee info with a 1-hour TTL.
   *
   * Schema: pk=<login>, sk=EMPLOYEE_INFO
   */
  public static async saveEmployeeInfo(loginName: string, employee: Employee): Promise<void> {
    const client = this.getClient();
    const ttl = Math.floor(Date.now() / 1000) + MEETING_CONSTANTS.EMPLOYEE_CACHE_TTL_SECONDS;

    await client.send(
      new PutItemCommand({
        TableName: TABLE_NAME,
        Item: marshall(
          {
            employee_alias: loginName.toLowerCase(),
            sk: 'EMPLOYEE_INFO',
            employeeData: employee,
            ttl,
            updatedAt: new Date().toISOString()
          },
          { removeUndefinedValues: true }
        )
      })
    );
  }

  // ── Global Stats ─────────────────────────────────────────────────────

  /** Atomically increment the global meeting counter by 1. */
  public static async incrementMeetingCounter(): Promise<void> {
    const client = this.getClient();
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({ employee_alias: 'GLOBAL_STATS', sk: 'MEETING_COUNT' }),
        UpdateExpression: 'ADD meetingCount :one',
        ExpressionAttributeValues: marshall({ ':one': 1 })
      })
    );
  }

  /** Get the current global meeting count. Returns 0 if the counter doesn't exist yet. */
  public static async getMeetingCount(): Promise<number> {
    const client = this.getClient();
    const result = await client.send(
      new GetItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({ employee_alias: 'GLOBAL_STATS', sk: 'MEETING_COUNT' })
      })
    );
    if (!result.Item) return 0;
    const item = unmarshall(result.Item);
    return item.meetingCount ?? 0;
  }
}
